/* ============================================================
   SV — About page. Horizon 3.5.1.

   Two jobs:
     1. Arm the scroll reveals. Content is visible by default and
        only becomes animated once this file runs — if the script
        never loads, nothing is left hidden.
     2. Parallax on the hero and statement images, with per-section
        strength.

   Fonts are not touched. Headings and paragraphs are real tags, so
   the theme styles them itself.
   ============================================================ */
(function () {
  'use strict';

  var doc = document.documentElement;
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ============ reveals ============ */

  var REVEAL_SEL = '.sv-rv, .sv-fade, .sv-mask';
  var io = null;

  function observer() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('sv-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -10% 0px' });
    return io;
  }

  function reveal(root) {
    var els = (root || document).querySelectorAll(REVEAL_SEL);
    if (!els.length) return;

    if (REDUCE || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('sv-in'); });
      return;
    }

    var seen = new Map();
    var obs = observer();

    els.forEach(function (el) {
      if (el.classList.contains('sv-in')) return;
      var n = seen.get(el.parentNode) || 0;
      el.style.transitionDelay = Math.min(n, 3) * 80 + 'ms';
      seen.set(el.parentNode, n + 1);
      obs.observe(el);
    });
  }

  /* ============ parallax ============ */

  var PX_SEL = '.sv-hero--px .sv-hero__img, .sv-statement--px .sv-statement__img';
  var HEADROOM = 0.13;
  var TRAVEL = 0.09;   // default when a section sets no strength
  var DESKTOP = 750;

  var frames = [], active = false, ticking = false;

  function collect() {
    frames = [].slice.call(document.querySelectorAll(PX_SEL)).map(function (img) {
      var box = img.parentNode;
      // Each section publishes its own strength as --sv-px (a percentage).
      var raw = parseFloat(getComputedStyle(box).getPropertyValue('--sv-px'));
      var travel = isNaN(raw) ? TRAVEL : Math.min(raw, 30) / 100;
      return { img: img, box: box, travel: travel };
    });
  }

  function prepare(f) {
    // Headroom is always a little more than the travel, so the image
    // can never expose an edge — and the scale never animates.
    var pad = Math.max(f.travel * 1.25, 0.02);
    var st = f.img.style;
    st.position = 'absolute';
    st.left = '0';
    st.top = (pad * -100).toFixed(2) + '%';
    st.width = '100%';
    st.height = ((1 + pad * 2) * 100).toFixed(2) + '%';
    st.willChange = 'transform';
    if (getComputedStyle(f.box).position === 'static') {
      f.box.style.position = 'relative';
    }
  }

  function reset(f) {
    var st = f.img.style;
    st.position = st.left = st.top = st.width = st.height = '';
    st.willChange = '';
    st.transform = '';
  }

  function apply() {
    var vh = window.innerHeight;
    frames.forEach(function (f) {
      var r = f.box.getBoundingClientRect();
      if (!r.height || r.bottom < -200 || r.top > vh + 200) return;
      var p = ((r.top + r.height / 2) - vh / 2) / (vh / 2 + r.height / 2);
      p = Math.max(-1, Math.min(1, p));
      f.img.style.transform =
        'translate3d(0,' + (p * r.height * f.travel).toFixed(2) + 'px,0)';
    });
    ticking = false;
  }

  function onScroll() {
    if (!ticking) { ticking = true; requestAnimationFrame(apply); }
  }

  function wanted() {
    if (REDUCE || window.innerWidth < DESKTOP) return false;
    // A section set to 0 strength opts itself out.
    return frames.some(function (f) { return f.travel > 0; });
  }

  function setMode() {
    var on = wanted();
    if (on === active) return;
    active = on;
    if (on) {
      frames.forEach(prepare);
      window.addEventListener('scroll', onScroll, { passive: true });
      apply();
    } else {
      window.removeEventListener('scroll', onScroll);
      frames.forEach(reset);
    }
  }

  function refresh() {
    collect();
    active = !wanted();
    setMode();
    if (active) onScroll();
  }

  /* ============ boot ============ */

  function init() {
    // Arm the animations only now — proof the script is running.
    doc.classList.add('sv-anim');
    collect();
    reveal();
    setMode();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', refresh);

  window.addEventListener('resize', function () {
    setMode();
    if (active) onScroll();
  }, { passive: true });

  document.addEventListener('shopify:section:load', function (e) {
    reveal(e.target);
    refresh();
  });
  document.addEventListener('shopify:section:unload', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
})();